import type { ReactNode } from 'react'
import { Alert, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { api } from '@/api/client'
import { BASE_MEU_ROBO, useMeuRobo, type Robo } from '@/api/robo'
import { useAuthStore } from '@/store/auth'

/**
 * O que vem ANTES da conversa com o robô: sem robô atribuído, robô ainda sem o
 * nome que o dono dá (é o batismo que abre o chat) e — com `cid` — conversa que
 * o servidor não reconhece como deste dono com ESTE robô (acesso revogado, ou o
 * robô trocou). Um link salvo para a DM cai aqui também, nunca numa tela em branco.
 */
export function RoboDmPortao({ cid, children }: { cid?: number; children: (robo: Robo) => ReactNode }) {
  const userId = useAuthStore((s) => s.user?.id)
  const { robo, carregando } = useMeuRobo()
  const nomeado = !!robo?.owner_named_at
  // A mesma checagem que a rota `ask` faz (`check`): a conversa é do dono com este
  // robô. O `ask` que volta 403/404 invalida esta chave (`useRoboAsk`).
  const conferencia = useQuery({
    queryKey: ['robo-dm-portao', userId, cid],
    enabled: !!cid && nomeado,
    queryFn: async () => (await api.get(`${BASE_MEU_ROBO}/conversations/${cid}`)).status,
    staleTime: 60_000,
    retry: (n, e) => n < 1 && !(isAxiosError(e) && [401, 403, 404].includes(e.response?.status ?? 0)),
  })
  if (carregando) return <Stack p="md"><Text c="dimmed">Carregando…</Text></Stack>
  if (!robo) return <Alert m="md" color="gray" title="Nenhum robô atribuído">
    Você não tem um robô atribuído. Fale com quem administra a frota para receber o seu.
  </Alert>
  if (!nomeado) return <Alert m="md" color="blue" title="Dê um nome ao seu robô">
    Ele está em {robo.tenant_name ?? `tenant ${robo.tenant_id}`} esperando por você.
    Dê um nome ao seu robô para começar a conversar — a conversa passa a ser com ele, por esse nome.
  </Alert>
  const negado = isAxiosError(conferencia.error) && [403, 404].includes(conferencia.error.response?.status ?? 0)
  if (negado) return <Alert m="md" color="orange" title={`${robo.name} · ${robo.tenant_name ?? ''}`}>
    Este contato exige acesso de leitura aos alertas de {robo.tenant_name ?? `tenant ${robo.tenant_id}`}.
  </Alert>
  return <>{children(robo)}</>
}
